import React, { useState, useEffect, useRef, useContext } from "react";
import { Property } from "../../../../service";
import { toast } from "react-hot-toast";
import OutlineButton from "../../common/Buttons/OutlineButton";
import { Formik, Form, FieldArray, getIn, useFormikContext } from "formik";
import FormField from "../../VendorOnboarding/FormField";
import * as Yup from "yup";
import Modal from "../../../Ui/Modal";
import ButtonComponent from "../../../Ui/Button";
import {
  propertyFieldConfig,
  createValidationSchemaProperties,
} from "../../../constant/Home/Properties";
import { useUser } from "../../../context/UserContext";
import useApiHandler from "../../../hooks/ApiHandler";
import { Unit } from "../../../../service";
import { useStatisticsData } from "../../../context/StatisticsData";

const AutoPopulateUnit = () => {
  const { values, setFieldValue } = useFormikContext();
  useEffect(() => {
    if (
      values.property_type === "Single Family" &&
      (!values.units ||
        values.units.length === 0 ||
        (values.units.length === 1 && values.units[0] === ""))
    ) {
      setFieldValue("units", ["Main Unit"]);
    }
  }, [values.property_type, setFieldValue, values.units]);
  return null;
};

const AddPropertyForm = ({
  setModalState,
  propertyId,
  onSuccess,
  setPropertyId,
}) => {
  const formikSubmitRef = useRef(null);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const { userData } = useUser();
  const { callApi, loading } = useApiHandler();
  const { refreshStatistics } = useStatisticsData();
  const fetchPropertyDetails = () => {
    if (propertyId) {
      callApi(() => Property.getPropertyById(propertyId), {
        onSuccess: (res) => {
          setPropertyDetails(res.data);
        },
        onError: (error) => {
          console.error("Error fetching property details:", error);
        },
        errorMessage: "Failed to fetch property details",
      });
    }
  };

  useEffect(() => {
    fetchPropertyDetails();
  }, [propertyId]);

  const handleSubmit = async (values, { resetForm }) => {
    const propertyData = {};
    propertyFieldConfig.forEach((field) => {
      propertyData[field.name] = values[field.name];
    });

    let payload = { ...propertyData };

    // Add units for both HOMEOWNER and LANDLORD account types

    if (propertyId) {
      await callApi(() => Property.editProperties(propertyId, payload), {
        onSuccess: (res) => {
          if (["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType)) {
            // setModalState("editTenant");
          }

          if (onSuccess) onSuccess();
          handleClose();
        },
        successMessage: "Property updated successfully!",
        errorMessage: "Failed to edit property",
      });
    } else {
      if (values.units && values.units.length > 0) {
        const extractedUnits = values.units.filter((u) => u && u.trim() !== "");
        if (extractedUnits.length > 0) {
          payload.units = extractedUnits;
        }
      }

      await callApi(() => Property.addProperties(payload), {
        onSuccess: (res) => {
          resetForm({ values: getInitialValues() });
          if (["HOMEOWNER", "STR_HOST", "FAMILY_MEMBER"].includes(userData?.accountType)) {
            setModalState("addFamilyMember");
          } else {
            setModalState("addTenant");
          }
          // Notify listeners across the app to refresh property lists immediately
          try {
            const newId = res?.data?.id;
            const evt = new CustomEvent("properties:refresh", {
              detail: { id: newId },
            });
            window.dispatchEvent(evt);
          } catch (_) {}

          if (onSuccess) onSuccess();
          setPropertyId(res.data?.id);
          refreshStatistics();

        },
        successMessage: "Property added successfully!",
        errorMessage: "Failed to add property",
      });
    }
  };

  const handleClose = () => {
    setPropertyId(null);
    setModalState("");
  };

  const getInitialValues = () => {
    const values = {};

    // Property fields
    propertyFieldConfig.forEach((field) => {
      if (propertyDetails) {
        values[field.name] =
          propertyDetails[field.name] ||
          propertyDetails[
          field.name === "property_type" ? "propertyType" : field.name
          ] ||
          "";
      } else {
        // For new properties, set default values based on account type
        if (
          field.name === "property_type" &&
          ["HOMEOWNER", "STR_HOST", "FAMILY_MEMBER"].includes(userData?.accountType)
        ) {
          values[field.name] = "Single Family";
        } else {
          values[field.name] = "";
        }
      }
    });

    // Units - only for LANDLORD account type
    if (["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType)) {
      if (propertyDetails?.units?.length) {
        values.units = propertyDetails.units.map((u) =>
          typeof u === "string" ? u : u.unit_apt || u.unitApt || u.unit || ""
        );
      } else {
        values.units = [""];
      }

      // Edit mode fields
      if (propertyId) {
        values.selectedUnitToEdit = "";
        values.newUnitName = "";
      }
    }

    return values;
  };

  // 🔑 Helper to mark all touched (including arrays)
  const markAllTouched = (errorsObj) => {
    if (Array.isArray(errorsObj)) {
      return errorsObj.map((err) => (err ? true : false));
    } else if (typeof errorsObj === "object" && errorsObj !== null) {
      const touchedObj = {};
      Object.keys(errorsObj).forEach((key) => {
        touchedObj[key] = markAllTouched(errorsObj[key]);
      });
      return touchedObj;
    }
    return true;
  };

  // Get unit options for edit mode - only for LANDLORD account type
  const getUnitOptions = () => {
    if (!["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType) || !propertyDetails?.units?.length)
      return [];

    const options = propertyDetails.units.map((unit, index) => {
      const option = {
        value:
          typeof unit === "string"
            ? unit
            : unit.id || unit.unitId || unit.unit_id || `Unit ${index + 1}`,
        label:
          typeof unit === "string"
            ? unit
            : unit.unit_apt || unit.unitApt || unit.unit || `Unit ${index + 1}`,
      };

      return option;
    });

    return options;
  };

  // Get filtered property type options based on account type
  const getPropertyTypeOptions = (field) => {
    if (
      field.name === "property_type" &&
      ["HOMEOWNER", "STR_HOST", "FAMILY_MEMBER"].includes(userData?.accountType)
    ) {
      // For HOMEOWNER, only show "Single Family" option
      return [
        {
          value: "Single Family",
          label: "Single Family",
        },
      ];
    }
    // For other account types, return all options
    return field.options || [];
  };

  return (
    <Modal
      open={true}
      onClose={handleClose}
      title={propertyId ? "Edit Property" : "Add Property"}
      maxWidth="756px"
      footer={
        <div className="flex w-full sm2:gap-4 gap-2 justify-end sm2:flex-row flex-col ">
          <ButtonComponent
            paddingY="10px"
            bgColor="#F8F8F8"
            color="#242424"
            onClick={() => {
              if (propertyId) {
                if (["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType)) {
                  setModalState("editTenant");
                } else if (["HOMEOWNER", "STR_HOST", "FAMILY_MEMBER"].includes(userData?.accountType)) {
                  setModalState("editFamily");
                } else {
                  setModalState("");
                  setPropertyId(null);
                }
              } else {
                handleClose();
              }
            }}
          >
            {propertyId
              ? ["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType)
                ? "Edit Tenant Details"
                : ["HOMEOWNER", "STR_HOST", "FAMILY_MEMBER"].includes(userData?.accountType)
                ? "Edit Family Details"
                : "Cancel"
              : "Cancel"}
          </ButtonComponent>
          {["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType) && propertyId && (
            <ButtonComponent
              paddingY="4px"
              bgColor="#3CAB5C"
              color="#fff"
              onClick={() =>
                formikSubmitRef.current && formikSubmitRef.current()
              }
            >
              Save
            </ButtonComponent>
          )}
          <ButtonComponent
            paddingY="10px"
            bgColor="#3CAB5C"
            color="#fff"
            onClick={() => {
              if (["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType) && propertyId) {
                // Exit button - set propertyId to null and modal state to null
                setPropertyId(null);
                setModalState(null);
              } else {
                // Save/Next button - submit form
                formikSubmitRef.current && formikSubmitRef.current();
              }
            }}
          >
            {propertyId
              ? ["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType)
                ? "Exit"
                : "Save"
              : "Next"}
          </ButtonComponent>
        </div>
      }
    >
      <Formik
        initialValues={getInitialValues()}
        enableReinitialize
        validationSchema={createValidationSchemaProperties(
          !!propertyId,
          userData?.accountType
        )}
        onSubmit={(values, formikHelpers) => handleSubmit(values, formikHelpers)}
      >
        {({
          submitForm,
          errors,
          touched,
          values,
          validateForm,
          setTouched,
          setFieldValue,
        }) => {
          formikSubmitRef.current = async () => {
            const validationErrors = await validateForm();
            if (Object.keys(validationErrors).length > 0) {
              setTouched(markAllTouched(validationErrors));
              return false;
            }
            return submitForm();
          };

          return (
            <Form>
              <AutoPopulateUnit />
              <div className="grid grid-cols-1 md:grid-cols-2 md:space-x-4 md:p-4 p-2">
                {/* Left Column */}
                <div className="space-y-4">
                  {propertyFieldConfig.slice(0, 3).map((field, index) => (
                    <div key={index}>
                      <FormField
                        label={field.label}
                        name={field.name}
                        type={field.type}
                        placeholder={field.placeholder}
                        options={getPropertyTypeOptions(field)}
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    </div>
                  ))}
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {propertyFieldConfig.slice(3).map((field, index) => (
                    <div key={index}>
                      <FormField
                        label={field.label}
                        name={field.name}
                        type={field.type}
                        placeholder={field.placeholder}
                        options={getPropertyTypeOptions(field)}
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    </div>
                  ))}
                </div>

                {/* Units Section - Full Width - Only show for LANDLORD account type */}
                {["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType) && (
                  <div style={{ marginLeft: "0" }} className="md:col-span-2">
                    <label className="text-[14px] text-[#333333] font-[600] mb-2 block">
                      {propertyId ? "Manage Units *" : "Add Units *"}
                    </label>

                    {propertyId ? (
                      // Edit Mode - Unit Selection and Edit
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-1 gap-0">
                          <FormField
                            label="Existing Unit to Edit (Optional)"
                            name="selectedUnitToEdit"
                            type="select"
                            placeholder="Select a unit to edit"
                            options={getUnitOptions()}
                            error={getIn(errors, "selectedUnitToEdit")}
                            touched={getIn(touched, "selectedUnitToEdit")}
                            onChange={(e) => {
                              const selectedUnitId = e.target.value;
                              setFieldValue(
                                "selectedUnitToEdit",
                                selectedUnitId
                              );

                              // Auto-fill the new unit name with current unit name
                              if (selectedUnitId && propertyDetails?.units) {
                                const selectedUnit = propertyDetails.units.find(
                                  (unit) =>
                                    (typeof unit === "string"
                                      ? unit
                                      : unit.id ||
                                      unit.unitId ||
                                      unit.unit_id) === selectedUnitId
                                );

                                if (selectedUnit) {
                                  const currentUnitName =
                                    typeof selectedUnit === "string"
                                      ? selectedUnit
                                      : selectedUnit.unit_apt ||
                                      selectedUnit.unitApt ||
                                      selectedUnit.unit ||
                                      "";
                                  setFieldValue("newUnitName", currentUnitName);
                                }
                              } else {
                                setFieldValue("newUnitName", "");
                              }
                            }}
                          />
                          <div className="flex gap-2 ">
                            <div className="flex-1">
                              <FormField
                                label="Unit Name"
                                name="newUnitName"
                                type="text"
                                placeholder="Enter unit name"
                                error={getIn(errors, "newUnitName")}
                                touched={getIn(touched, "newUnitName")}
                              />
                            </div>
                            <button
                              type="button"
                              disabled={
                                !values.newUnitName ||
                                loading
                              }
                              onClick={async () => {
                                // Validate that name is filled
                                if (
                                  !values.newUnitName
                                ) {
                                  toast.error(
                                    "Please enter a unit name"
                                  );
                                  return;
                                }

                                try {
                                  if (values.selectedUnitToEdit) {
                                    // Edit Mode - Update existing unit
                                    await callApi(
                                      () =>
                                        Unit.editUnit(values.selectedUnitToEdit, {
                                          unit_apt: values.newUnitName,
                                          property: propertyId, // Use the actual property ID
                                        }),
                                      {
                                        onSuccess: (res) => {
                                          // Refresh property details after successful edit
                                          fetchPropertyDetails();
                                          // Clear the form
                                          setFieldValue("selectedUnitToEdit", "");
                                          setFieldValue("newUnitName", "");
                                        },
                                        successMessage:
                                          "Unit updated successfully!",
                                        errorMessage: "Failed to update unit",
                                      }
                                    );
                                  } else {
                                    // Add Mode - Add a new unit to existing property
                                    await callApi(
                                      () =>
                                        Unit.addUnit({
                                          unit_apt: values.newUnitName,
                                          property: propertyId,
                                        }),
                                      {
                                        onSuccess: (res) => {
                                          fetchPropertyDetails();
                                          setFieldValue("newUnitName", "");
                                        },
                                        successMessage: "Unit added successfully!",
                                        errorMessage: "Failed to add unit",
                                      }
                                    );
                                  }
                                } catch (error) {
                                  console.error("Error managing unit:", error);
                                }
                              }}
                              className="px-3 py-1 text-[14px] mt-6 font-medium bg-[#D4B1001A] text-[#D4B100] rounded-[8px] border h-[30px] border-[#D4B100] hover:bg-yellow-50 shadow-md hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {loading ? "Saving..." : values.selectedUnitToEdit ? "Save" : "Add"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Add Mode - Original unit management
                      <FieldArray name="units">
                        {({ push, remove }) => (
                          <div className="flex flex-col gap-3">
                            {values.units.map((unit, idx) => (
                              <div
                                key={idx}
                                className="flex md:gap-2 gap-1 sm2:flex-row flex-col"
                              >
                                <div className="flex-1">
                                  <FormField
                                    label=""
                                    name={`units.${idx}`}
                                    type="text"
                                    placeholder="Enter New Unit Name"
                                    error={getIn(errors, `units.${idx}`)}
                                    touched={getIn(touched, `units.${idx}`)}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => remove(idx)}
                                  className="px-3 py-1 max-w-[75px] w-full text-sm mt-1 text-red-600 border h-[36px] rounded-[8px] border-red-300 hover:bg-red-50"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}

                            <div className="flex justify-center">
                              <OutlineButton
                                label="Add Additional Unit"
                                onClick={() => push("")}
                              />
                            </div>
                          </div>
                        )}
                      </FieldArray>
                    )}
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddPropertyForm;
